import React, { useState, useEffect } from "react";
import "../../../css/CRUDModal.css";
import ButtonTriangle from "../../../assets/Icons/Button Triangle.png";
import { useForm, Controller } from "react-hook-form";
import { Box, CircularProgress } from "@mui/material";
import FilterIcon from "../../../assets/Icons/Orange Filter.png";
import CrossIcon from "../../../assets/Icons/cross_svg.svg";
import LeftCircle from "../../../assets/Icons/Left circle B.png";
import RightCircle from "../../../assets/Icons/Right circle A.png";
import Dropdown from "../../../components/Dropdown/index.jsx";
import { axiosGetApi, axiosPostApi } from "../../../helpers/interceptorServices";
import { apiList } from "../../../helpers/apiList";

// Helper function to get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const BuyStationaryFilterModal = ({
  show,
  handleClose,
  filterData,
  setFilterData,
  handleReset,
  setCurrentPage,
  fetchData,
  dataList = [],
}) => {
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      itemName: filterData.itemName || "",
      createdBy: filterData.createdBy || "",
      fromDate: filterData.fromDate || "",
      toDate: filterData.toDate || "",
      status: filterData.status || "",
    },
    mode: "onChange",
  });

  const watchedValues = watch();

  const [selectedTab, setSelectedTab] = useState("Stationary Name");
  const [loading, setLoading] = useState(false);
  const [stationaryList, setStationaryList] = useState([]);
  const [createdByUserList, setCreatedByUserList] = useState([]);
  const loggedInUserName =
    sessionStorage.getItem("username") ||
    sessionStorage.getItem("userId") ||
    "";

  useEffect(() => {
    fetchStationaryItems();
    fetchCreatedByUsers();
  }, []);

  const fetchCreatedByUsers = async () => {
    try {
      const response = await axiosPostApi(
        `${apiList.FETCH_BUY_STATIONARY_MASTER}?pageSize=500&pageNo=1`,
        { username: loggedInUserName },
      );
      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00" &&
        Array.isArray(response?.data?.data)
      ) {
        const usersFromRequests = response.data.data
          .map((item) => {
            const id = item?.lmbpUsername || item?.createdBy || "";
            const name = item?.lmbpName || "";
            if (!id && !name) return null;
            const label = id && name ? `${id} - ${name}` : id || name;
            return {
              label: label.trim(),
              value: label.trim(),
              userId: id,
              userName: name,
            };
          })
          .filter(Boolean);

        setCreatedByUserList((prev) => {
          const map = new Map();
          [...prev, ...usersFromRequests].forEach((u) => {
            if (u && u.label && !map.has(u.label)) {
              map.set(u.label, u);
            }
          });
          return Array.from(map.values());
        });
      }
    } catch (err) {
      console.error("Error fetching created by users from requests:", err);
    }
  };

  const fetchStationaryItems = async () => {
    try {
      const response = await axiosGetApi(
        `${apiList.GET_STATIONARY_ITEMS_DETAILS}?username=${loggedInUserName}`,
      );
      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        const sorted = (response?.data?.data || []).sort((a, b) => {
          const nameA = a.itemName || a.itemCode || "";
          const nameB = b.itemName || b.itemCode || "";
          return nameA.trim().localeCompare(nameB.trim(), undefined, {
            sensitivity: "base",
          });
        });
        setStationaryList(sorted);
      }
    } catch (err) {
      console.error("Error fetching stationary items:", err);
    }
  };

  const sortedStationaryList = React.useMemo(() => {
    return [...stationaryList].sort((a, b) => {
      const nameA = a.itemName || a.itemCode || "";
      const nameB = b.itemName || b.itemCode || "";
      return nameA.trim().localeCompare(nameB.trim(), undefined, {
        sensitivity: "base",
      });
    });
  }, [stationaryList]);

  const stationaryOptions = React.useMemo(() => {
    const list = sortedStationaryList.map((item) => {
      const displayName = item.itemName || item.itemCode;
      return {
        label: displayName,
        value: displayName,
        itemCode: item.itemCode,
        itemName: displayName,
      };
    });
    if (watchedValues.itemName) {
      return [
        {
          label: "✕ Clear Stationary Selection",
          value: "",
          itemName: "",
          itemCode: "",
        },
        ...list,
      ];
    }
    return list;
  }, [sortedStationaryList, watchedValues.itemName]);

  const combinedCreatedByUserList = React.useMemo(() => {
    const map = new Map();

    // 1. From prop dataList (the current table records, e.g. ATC46186 - MANJEET KUMAR)
    if (Array.isArray(dataList)) {
      dataList.forEach((item) => {
        const id =
          item?.lmbpUserCode || item?.lmbpUsername || item?.createdBy || "";
        const name = item?.lmbpName || "";
        let label = "";
        if (
          typeof item?.lmbpUsername === "string" &&
          item.lmbpUsername.trim() !== ""
        ) {
          label = item.lmbpUsername.trim();
        } else if (id && name) {
          label = `${id} - ${name}`;
        } else {
          label = id || name;
        }

        if (label) {
          map.set(label, {
            label,
            value: label,
            userId: id,
            userName: name,
          });
        }
      });
    }

    // 2. From fetched requests / users
    if (Array.isArray(createdByUserList)) {
      createdByUserList.forEach((item) => {
        if (item && item.label && !map.has(item.label)) {
          map.set(item.label, item);
        }
      });
    }

    return Array.from(map.values());
  }, [dataList, createdByUserList]);

  const sortedCreatedByUserList = React.useMemo(() => {
    return [...combinedCreatedByUserList].sort((a, b) => {
      const nameA = a.label || a.value || "";
      const nameB = b.label || b.value || "";
      return nameA.trim().localeCompare(nameB.trim(), undefined, {
        sensitivity: "base",
      });
    });
  }, [combinedCreatedByUserList]);

  const createdByOptions = React.useMemo(() => {
    const list = sortedCreatedByUserList.map((item) => ({
      label: item.label,
      value: item.value,
      userId: item.userId,
      userName: item.userName,
    }));
    if (watchedValues.createdBy) {
      return [
        {
          label: "✕ Clear Created By Selection",
          value: "",
          userId: "",
          userName: "",
        },
        ...list,
      ];
    }
    return list;
  }, [sortedCreatedByUserList, watchedValues.createdBy]);

  // Options for Status dropdown
  const statusOptions = [
    { label: "Pending", value: "PENDING" },
    { label: "Approved", value: "APPROVED" },
    { label: "Rejected", value: "REJECTED" },
    { label: "Cancelled", value: "CANCELLED" },
  ];

  const hasActiveFilter = (tabName) => {
    switch (tabName) {
      case "Stationary Name":
        return Boolean(watchedValues.itemName && watchedValues.itemName.trim() !== "");
      case "Created By":
        return Boolean(watchedValues.createdBy && watchedValues.createdBy.trim() !== "");
      case "Created Date":
        return (
          (watchedValues.fromDate && watchedValues.fromDate !== "") ||
          (watchedValues.toDate && watchedValues.toDate !== "")
        );
      case "Status":
        return Boolean(watchedValues.status && watchedValues.status !== "");
      default:
        return false;
    }
  };

  const handleTabChange = (e, tabName) => {
    e.preventDefault();
    setSelectedTab(tabName);
  };

  const getAvailableTabs = () => {
    return ["Stationary Name", "Created By", "Created Date", "Status"];
  };

  const onSubmit = (data) => {
    const formattedData = {
      itemName: data.itemName || "",
      createdBy: data.createdBy ? data.createdBy.trim() : "",
      fromDate: data.fromDate || "",
      toDate: data.toDate || "",
      status: data.status || "",
      isActive: null,
    };

    setFilterData(formattedData);
    setCurrentPage(1);
    fetchData(formattedData);
    handleClose();
  };

  const onReset = () => {
    reset({
      itemName: "",
      createdBy: "",
      fromDate: "",
      toDate: "",
      status: "",
    });

    const defaultFilterData = {
      itemName: "",
      createdBy: "",
      fromDate: "",
      toDate: "",
      status: "",
      isActive: null,
    };

    setFilterData(defaultFilterData);
    handleReset();
  };

  // Reset selected tab when modal opens
  useEffect(() => {
    if (show) {
      setSelectedTab("Stationary Name");
      setValue("itemName", filterData.itemName || "");
      setValue("createdBy", filterData.createdBy || "");
      setValue("fromDate", filterData.fromDate || "");
      setValue("toDate", filterData.toDate || "");
      setValue("status", filterData.status || "");
    }
  }, [show, filterData, setValue]);

  return (
    <>
      {show && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1060,
          }}
        >
          <div className="modal-dialog" role="document">
            <div
              className="modal-content"
              style={{
                width: "40vw",
                borderRadius: "24px",
                padding: "24px",
                backgroundColor: "#FFF",
                border: "1px solid rgba(0, 0, 0, 0.175)",
              }}
            >
              <img
                src={LeftCircle}
                alt="leftImg"
                className="absolute left-0 bottom-0 w-[110px] h-[105px]"
              />
              <img
                src={RightCircle}
                alt="rightImg"
                className="absolute right-0 top-0 w-[110px] h-[100px]"
              />
              <div
                className="modal-header"
                style={{
                  padding: "1rem 1rem",
                  borderBottom: "1px solid #dee2e6",
                  justifyContent: "space-between",
                  display: "flex",
                }}
              >
                <div className="flex items-center gap-2">
                  <img src={FilterIcon} alt="filter icon" className="w-6 h-6" />
                  <h5 className="modal-title main-title font-medium">
                    Filters
                  </h5>
                </div>
                <img
                  src={CrossIcon}
                  alt="close icon"
                  className="w-[30px] h-[30px] cursor-pointer z-20"
                  onClick={handleClose}
                />
              </div>
              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <div
                  className="scoped-modal modal-body"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <div className="tab-container">
                    <ul className="tab-list">
                      {getAvailableTabs().map((tab) => (
                        <div className="tab-wrapper" key={tab}>
                          <li
                            className={
                              selectedTab === tab
                                ? "tab-item selected"
                                : "tab-item"
                            }
                            style={
                              hasActiveFilter(tab) && selectedTab !== tab
                                ? {
                                    outline: "2px solid #FF6B35",
                                    outlineOffset: "2px",
                                  }
                                : {}
                            }
                          >
                            <button
                              className={`tab-button ${
                                selectedTab === tab ? "active" : ""
                              }`}
                              onClick={(e) => handleTabChange(e, tab)}
                            >
                              {tab}
                            </button>
                          </li>
                          {selectedTab === tab && (
                            <img
                              src={ButtonTriangle}
                              alt="tri"
                              className="tab-triangle"
                            />
                          )}
                        </div>
                      ))}
                    </ul>

                    <div className="tab-content !bg-[linear-gradient(to_bottom,#FFE8D4_0%_100%,transparent_25%_100%)]">
                      {selectedTab === "Stationary Name" && (
                        <div className="dateField">
                          <label className="dateLabel">
                            Stationary Name
                          </label>
                          <div className="dateInputWrapper">
                            <Dropdown
                              placeholder="Select Stationary Name"
                              options={stationaryOptions}
                              value={watchedValues.itemName || ""}
                              labelKey="label"
                              valueKey="value"
                              showSearch={true}
                              selectClassName="!h-[38px] !rounded-[6px]"
                              onChange={(item) => {
                                const selectedName = item
                                  ? item.itemName || item.value || ""
                                  : "";
                                setValue("itemName", selectedName);
                                setFilterData((prev) => ({
                                  ...prev,
                                  itemName: selectedName,
                                }));
                              }}
                            />
                            {errors.itemName && (
                              <div className="error-text">
                                {errors.itemName.message}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {selectedTab === "Created By" && (
                        <div className="dateField">
                          <label className="dateLabel">Created By</label>
                          <div className="dateInputWrapper">
                            <Dropdown
                              placeholder="Select Created By"
                              options={createdByOptions}
                              value={watchedValues.createdBy || ""}
                              labelKey="label"
                              valueKey="value"
                              showSearch={true}
                              selectClassName="!h-[38px] !rounded-[6px]"
                              onChange={(item) => {
                                const selectedVal = item
                                  ? item.value || ""
                                  : "";
                                setValue("createdBy", selectedVal);
                                setFilterData((prev) => ({
                                  ...prev,
                                  createdBy: selectedVal,
                                }));
                              }}
                            />
                            {errors.createdBy && (
                              <div className="error-text">
                                {errors.createdBy.message}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {selectedTab === "Created Date" && (
                        <div
                          className="dateGrid"
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "1rem",
                            alignItems: "flex-start",
                          }}
                        >
                          <Controller
                            name="fromDate"
                            control={control}
                            rules={{
                              validate: {
                                validDate: (value) =>
                                  !value ||
                                  !isNaN(Date.parse(value)) ||
                                  "Invalid From Date",
                                notFuture: (value) =>
                                  !value ||
                                  new Date(value) <= new Date() ||
                                  "From Date cannot be in the future",
                                beforeToDate: (value, formValues) =>
                                  !value ||
                                  !formValues.toDate ||
                                  new Date(value) <=
                                    new Date(formValues.toDate) ||
                                  "From Date must be before To Date",
                                requiredIfToDate: (value, formValues) =>
                                  !formValues.toDate ||
                                  !!value ||
                                  "From Date is required when To Date is selected",
                              },
                            }}
                            render={({ field }) => (
                              <div className="dateField" style={{ flex: 1 }}>
                                <label className="dateLabel">From Date</label>
                                <div className="dateInputWrapper">
                                  <input
                                    {...field}
                                    type="date"
                                    className="dateInput"
                                    max={getTodayDate()}
                                    onChange={(e) => {
                                      field.onChange(e.target.value);
                                    }}
                                  />
                                  {errors.fromDate && (
                                    <div className="error-text">
                                      {errors.fromDate.message}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          />
                          <Controller
                            name="toDate"
                            control={control}
                            rules={{
                              validate: {
                                validDate: (value) =>
                                  !value ||
                                  !isNaN(Date.parse(value)) ||
                                  "Invalid To Date",
                                notFuture: (value) =>
                                  !value ||
                                  new Date(value) <= new Date() ||
                                  "To Date cannot be in the future",
                                afterFromDate: (value, formValues) =>
                                  !value ||
                                  !formValues.fromDate ||
                                  new Date(value) >=
                                    new Date(formValues.fromDate) ||
                                  "To Date must be after From Date",
                                requiredIfFromDate: (value, formValues) =>
                                  !formValues.fromDate ||
                                  !!value ||
                                  "To Date is required when From Date is selected",
                              },
                            }}
                            render={({ field }) => (
                              <div className="dateField" style={{ flex: 1 }}>
                                <label className="dateLabel">To Date</label>
                                <div className="dateInputWrapper">
                                  <input
                                    {...field}
                                    type="date"
                                    className="dateInput"
                                    max={getTodayDate()}
                                    onChange={(e) => {
                                      field.onChange(e.target.value);
                                    }}
                                  />
                                  {errors.toDate && (
                                    <div className="error-text">
                                      {errors.toDate.message}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          />
                        </div>
                      )}

                      {selectedTab === "Status" && (
                        <Controller
                          name="status"
                          control={control}
                          render={({ field }) => (
                            <div className="dateField">
                              <Dropdown
                                label="Status"
                                options={statusOptions}
                                value={field.value}
                                labelKey="label"
                                valueKey="value"
                                placeholder="Select Status"
                                onChange={(item) => {
                                  field.onChange(item.value);
                                }}
                              />
                            </div>
                          )}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className="scoped-modal"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "12px",
                    paddingBottom: "0px",
                    gap: "16px",
                    borderTop: "1px solid #dee2e6",
                  }}
                >
                  <div className="buttonContainer">
                    <button
                      type="button"
                      className="btn btn-back !rounded-lg w-[133px]"
                      onClick={onReset}
                      disabled={loading}
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="buttonContainer">
                    <button
                      type="submit"
                      className="btn btn-submit !rounded-lg w-[133px]"
                      disabled={loading}
                    >
                      {loading ? (
                        <CircularProgress
                          style={{ width: "20px", height: "20px" }}
                        />
                      ) : (
                        "Apply"
                      )}
                    </button>
                  </div>
                </div>
              </Box>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BuyStationaryFilterModal;
